import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AttackChainVisualization from '../components/visualization/AttackChainVisualization';
import ActionDetailModal from '../components/common/ActionDetailModal';
import actionService from '../services/actionService';
import playbookService from '../services/playbookService';
import projectService from '../services/projectService';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';

function AttackChainView() {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [actions, setActions] = useState([]);
  const [playbooks, setPlaybooks] = useState([]);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewSource, setViewSource] = useState('actions'); // 'actions' or 'playbook'
  const [selectedAction, setSelectedAction] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Load project actions
        if (projectId) {
          const [projectData, actionsData] = await Promise.all([
            projectService.getProject(projectId),
            actionService.getActionsByProject(projectId)
          ]);
          setProject(projectData);
          setActions(Array.isArray(actionsData) ? actionsData : []);
        } else {
          // Load all actions with MITRE techniques
          const actionsData = await actionService.getActions();
          const actionsArray = Array.isArray(actionsData) ? actionsData : [];
          setActions(actionsArray.filter(a => a.mitreAttackTechnique || a.mitre_technique));
        }

        // Load playbooks
        const playbooksData = await playbookService.getPlaybooks({ project: projectId });
        setPlaybooks(Array.isArray(playbooksData) ? playbooksData : []);

        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  const handlePlaybookSelect = async (playbookId) => {
    if (!playbookId) {
      setSelectedPlaybook(null);
      setViewSource('actions');
      return;
    }

    try {
      const playbook = await playbookService.getPlaybook(playbookId);
      setSelectedPlaybook(playbook);
      setViewSource('playbook');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleNodeClick = (data) => {
    setSelectedAction(data);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      {projectId && (
        <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 flex items-center text-sm mb-2">
          ← {t('attackChain.backToProject')}
        </Link>
      )}
      <PageHeader
        title={t('attackChain.title')}
        subtitle={project ? `${t('layout.project')}: ${project.name}` : t('attackChain.subtitle')}
      >
        {projectId && (
          <Link
            to={`/projects/${projectId}/actions/new`}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            + {t('actions.logAction')}
          </Link>
        )}
      </PageHeader>

      {error && (
        <div className={`border px-4 py-3 rounded mb-4 ${darkMode ? 'bg-red-900/50 border-red-500 text-red-200' : 'bg-red-50 border-red-300 text-red-700'}`}>
          {error}
        </div>
      )}

      {/* Controls */}
      <div className={`rounded-lg p-4 mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <div className="flex flex-wrap gap-4 items-center">
          <div>
            <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('attackChain.dataSource')}</label>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setViewSource('actions');
                  setSelectedPlaybook(null);
                }}
                className={`px-4 py-2 rounded ${
                  viewSource === 'actions' ? 'bg-red-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}
              >
                {t('attackChain.projectActions')} ({actions.length})
              </button>
              <button
                onClick={() => setViewSource('playbook')}
                className={`px-4 py-2 rounded ${
                  viewSource === 'playbook' ? 'bg-red-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                }`}
                disabled={playbooks.length === 0}
              >
                {t('attackChain.playbookSteps')}
              </button>
            </div>
          </div>

          {viewSource === 'playbook' && (
            <div className="flex-1 min-w-[200px]">
              <label className={`block text-sm mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('attackChain.selectPlaybook')}</label>
              <select
                value={selectedPlaybook?._id || ''}
                onChange={(e) => handlePlaybookSelect(e.target.value)}
                className={`w-full px-3 py-2 rounded ${darkMode ? 'bg-gray-700 text-white' : 'bg-white border border-gray-300 text-gray-900'}`}
              >
                <option value="">{t('attackChain.selectPlaybook')}...</option>
                {playbooks.map((pb) => (
                  <option key={pb._id} value={pb._id}>
                    {pb.name} ({pb.steps?.length || 0} steps)
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('attackChain.totalSteps')}</div>
          <div className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {viewSource === 'actions' ? actions.length : selectedPlaybook?.steps?.length || 0}
          </div>
        </div>
        <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('attackChain.tacticsCovered')}</div>
          <div className="text-2xl font-bold text-blue-500">
            {viewSource === 'actions'
              ? new Set(actions.map(a => a.mitreAttackTechnique?.tactic || a.mitre_technique?.tactics?.[0]).filter(Boolean)).size
              : new Set(selectedPlaybook?.steps?.map(s => s.tactic).filter(Boolean)).size || 0
            }
          </div>
        </div>
        <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('attackChain.techniquesUsed')}</div>
          <div className="text-2xl font-bold text-purple-500">
            {viewSource === 'actions'
              ? new Set(actions.map(a => a.mitreAttackTechnique?.id || a.mitre_technique?.technique_id).filter(Boolean)).size
              : new Set(selectedPlaybook?.steps?.map(s => s.techniqueId).filter(Boolean)).size || 0
            }
          </div>
        </div>
        <div className={`rounded-lg p-4 ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('reports.successRate')}</div>
          <div className="text-2xl font-bold text-green-500">
            {viewSource === 'actions' && actions.length > 0
              ? Math.round(
                  (actions.filter(a => a.result === 'successful' || a.result === 'detected').length /
                    actions.length) *
                    100
                )
              : 0}
            %
          </div>
        </div>
      </div>

      {/* Visualization */}
      <AttackChainVisualization
        actions={viewSource === 'actions' ? actions : null}
        playbook={viewSource === 'playbook' ? selectedPlaybook : null}
        height={500}
        onNodeClick={handleNodeClick}
      />

      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedAction}
        isOpen={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        projectId={projectId}
        onActionUpdated={(updated) => {
          setSelectedAction(updated);
          setActions(prev => prev.map(a => a._id === updated._id ? updated : a));
        }}
      />

      {/* Actions/Steps Table */}
      <div className={`mt-6 rounded-lg overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
        <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {viewSource === 'actions' ? t('attackChain.actionsSequence') : t('attackChain.playbookSteps')}
          </h3>
        </div>
        <table className="w-full">
          <thead className={darkMode ? 'bg-gray-900' : 'bg-gray-50'}>
            <tr>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>#</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('common.name')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('attackChain.technique')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('attackChain.tactic')}</th>
              <th className={`text-left px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600 font-semibold'}`}>{t('common.status')}</th>
            </tr>
          </thead>
          <tbody>
            {(viewSource === 'actions' ? actions : selectedPlaybook?.steps || []).map((item, index) => (
              <tr
                key={item._id || item.id || index}
                className={`border-t cursor-pointer ${darkMode ? 'border-gray-700 hover:bg-gray-750' : 'border-gray-200 hover:bg-gray-50'}`}
                onClick={() => setSelectedAction(item)}
              >
                <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                <td className={`px-4 py-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  {item.title || item.name}
                </td>
                <td className="px-4 py-3">
                  <span className="text-red-500">
                    {item.mitreAttackTechnique?.id || item.mitre_technique?.technique_id || item.techniqueId || '-'}
                  </span>
                </td>
                <td className={`px-4 py-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {item.mitreAttackTechnique?.tactic || item.mitre_technique?.tactics?.[0] || item.tactic || '-'}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-xs ${
                    item.result === 'successful' || item.status === 'completed' ? 'bg-green-100 text-green-800' :
                    item.result === 'detected' ? 'bg-yellow-100 text-yellow-800' :
                    item.result === 'blocked' || item.status === 'failed' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {item.result || item.status || 'pending'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AttackChainView;
